import { useSyncExternalStore } from 'react';

import { preferenceStorage } from '@/privacy/consent';

import { type DevicePlatform, detectPlatform, isIosSafari, isStandalone } from './platform';

/**
 * Estado da instalação do Live Gospel como aplicativo (PWA), fora do React: o navegador avisa
 * que pode instalar (`beforeinstallprompt`) logo ao carregar, muitas vezes antes de a
 * primeira tela montar. `initPwa()` roda no `main.tsx` e guarda o evento; as telas leem
 * com `usePwa()`.
 *
 * O que fica neste navegador (`localStorage`, por conveniência — some com os dados do
 * site; só com os cookies funcionais aceitos): o "Agora não" do convite e o "Já adicionei" do iPhone, que o Safari não avisa.
 */

/** O evento do Chrome/Edge que abre o "Instalar app?" do navegador. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PwaState {
  platform: DevicePlatform;
  /** iPhone/iPad no Safari, o navegador que adiciona à Tela de Início em qualquer iOS. */
  iosSafari: boolean;
  /** O navegador ofereceu a instalação: "Instalar agora" abre o aviso dele. */
  canPrompt: boolean;
  /** Aberto pelo ícone, instalado nesta sessão ou marcado como adicionado. */
  installed: boolean;
  /** Aberto pelo ícone da tela inicial agora. */
  standalone: boolean;
  /** "Agora não" no convite: o banner some (o menu do perfil continua oferecendo). */
  dismissed: boolean;
  /** Há versão nova do Live Gospel baixada, esperando "Atualizar". */
  updateReady: boolean;
}

export const PWA_KEYS = {
  dismissed: 'escala:pwa:dismissed',
  installed: 'escala:pwa:installed',
} as const;

function read(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** Cookie funcional (`escala:pwa:*`): sem o consentimento, vale só nesta sessão. */
function write(key: string, on: boolean) {
  if (on) preferenceStorage.set(key, '1');
  else preferenceStorage.remove(key);
}

let state: PwaState | null = null;
let deferred: BeforeInstallPromptEvent | null = null;
let applyUpdate: (() => Promise<void>) | null = null;
let detach: (() => void) | null = null;
const listeners = new Set<() => void>();

function set(patch: Partial<PwaState>) {
  state = { ...current(), ...patch };
  listeners.forEach((l) => l());
}

function current(): PwaState {
  if (!state) initPwa();

  return state!;
}

/** Lê o aparelho e passa a ouvir o navegador. Idempotente. */
export function initPwa(win: Window = window): void {
  if (state) return;

  const standalone = isStandalone(win);

  state = {
    platform: detectPlatform(win.navigator),
    iosSafari: isIosSafari(win.navigator),
    canPrompt: false,
    installed: standalone || read(PWA_KEYS.installed),
    standalone,
    dismissed: read(PWA_KEYS.dismissed),
    updateReady: false,
  };

  const onPrompt = (event: Event) => {
    // Sem o `preventDefault`, o Chrome mostra a própria barra; o Live Gospel oferece no seu tempo.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    set({ canPrompt: true });
  };
  const onInstalled = () => {
    deferred = null;
    write(PWA_KEYS.installed, true);
    set({ canPrompt: false, installed: true });
  };

  win.addEventListener('beforeinstallprompt', onPrompt);
  win.addEventListener('appinstalled', onInstalled);
  detach = () => {
    win.removeEventListener('beforeinstallprompt', onPrompt);
    win.removeEventListener('appinstalled', onInstalled);
  };
}

/** Abre o "Instalar app?" do navegador. `unavailable`: o navegador não ofereceu. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;

  if (!event) return 'unavailable';

  // O evento só serve uma vez; se a pessoa recusar, o navegador oferece de novo depois.
  deferred = null;
  set({ canPrompt: false });
  await event.prompt();

  const { outcome } = await event.userChoice;

  if (outcome === 'accepted') {
    write(PWA_KEYS.installed, true);
    set({ installed: true });
  }

  return outcome;
}

export function dismissInstallBanner(): void {
  write(PWA_KEYS.dismissed, true);
  set({ dismissed: true });
}

/** "Já adicionei" (iPhone) ou "Já instalei" (passo a passo manual). */
export function markInstalled(): void {
  write(PWA_KEYS.installed, true);
  set({ installed: true });
}

/** O service worker baixou uma versão nova: `apply` troca e recarrega a página. */
export function offerUpdate(apply: () => Promise<void>): void {
  applyUpdate = apply;
  set({ updateReady: true });
}

export async function updateNow(): Promise<void> {
  set({ updateReady: false });
  await applyUpdate?.();
}

export function dismissUpdate(): void {
  set({ updateReady: false });
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

export function usePwa(): PwaState {
  return useSyncExternalStore(subscribe, current, current);
}

/** O convite (banner) aparece no celular, ou onde o navegador oferece a instalação. */
export function showsInstallInvite(s: PwaState): boolean {
  return !s.installed && !s.dismissed && (s.platform !== 'desktop' || s.canPrompt);
}

/** Só para os testes: esquece o estado e os ouvintes. */
export function resetPwa(): void {
  detach?.();
  detach = null;
  state = null;
  deferred = null;
  applyUpdate = null;
}
